import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import { ListItemText, Menu, MenuItem, type MenuListProps } from '@mui/material'

import type { CRUDOrderOptions, Order, PrescriptionTimelineItem } from '~/types'
import type { Prescription } from '~/types/entities/prescriptions'
import { getIdFromTimelineItem } from '~/utils/prescriptionUtils'

import { usePrescriptionCardActions } from '../usePrescriptionCardActions'

const MENU_ID_PREFIX = 'rx-order-table-menu'
const BUTTON_ID_PREFIX = 'rx-order-table-menu-button'

interface ExtendedMenuListProps extends MenuListProps {
  'data-click-exclude'?: string
}

interface MenuActionItem {
  key: string
  label: string
  onClick: (e: React.MouseEvent) => void
}

export interface RxOrderTableMenuProps {
  index?: number
  isInvoice?: boolean
  item: PrescriptionTimelineItem
  onUpdateRefill?: (refill: Order, options: CRUDOrderOptions) => void
  refill?: Prescription
  showPrint?: boolean
}

const RxOrderTableMenu = ({
  item,
  refill,
  isInvoice = false,
  index = 0,
  onUpdateRefill,
  showPrint = true,
}: RxOrderTableMenuProps) => {
  const { t } = useTranslation(['Common'])
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const {
    handlePrint,
    handleViewInvoice,
    handleViewRefill,
    handlePreviewPrescription,
    handlePreviewRefill,
    getActionVisibility,
    getSoapUrl,
    persistClickedItem,
  } = usePrescriptionCardActions({
    item,
    index,
    isInvoice,
    onUpdateRefill,
    refill,
    showPrint,
  })

  const visibility = getActionVisibility()

  const prescription = refill ?? item.prescription
  const prescriptionId = prescription?.id
  if (!visibility.canShowMenu || !prescriptionId) return null

  const hasVisibleItems =
    visibility.shouldShowPrint ||
    visibility.shouldShowInvoice ||
    visibility.shouldShowPreview ||
    visibility.shouldShowSoap

  if (!hasVisibleItems) return null

  const menuId = `${MENU_ID_PREFIX}-${prescriptionId}`
  const buttonId = `${BUTTON_ID_PREFIX}-${prescriptionId}`

  const open = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => setAnchorEl(null)

  const buildMenuItems = (): MenuActionItem[] => {
    const menuItems: MenuActionItem[] = []

    if (visibility.shouldShowPrint) {
      const printLabel = t('Common:PRINT_ACTION')
      menuItems.push({
        key: printLabel,
        label: printLabel,
        onClick: (e) => {
          e.stopPropagation()
          handlePrint(
            refill
              ? { ...refill, parent: item.prescription }
              : item.prescription,
          )
          handleMenuClose()
        },
      })
    }

    if (visibility.shouldShowInvoice) {
      const invoiceLabel = t('Common:VIEW_INVOICE')
      menuItems.push({
        key: invoiceLabel,
        label: invoiceLabel,
        onClick: (e) => {
          e.stopPropagation()
          if (refill) {
            handleViewRefill(refill)
          } else {
            handleViewInvoice(getIdFromTimelineItem(item))
          }
          handleMenuClose()
        },
      })
    }

    if (visibility.shouldShowPreview) {
      const previewLabel = t('Common:PREVIEW_ACTION')
      menuItems.push({
        key: previewLabel,
        label: previewLabel,
        onClick: (e) => {
          e.stopPropagation()
          if (refill) {
            handlePreviewRefill(refill)
          } else {
            handlePreviewPrescription()
          }
          handleMenuClose()
        },
      })
    }

    if (visibility.shouldShowSoap) {
      const soapId = refill?.soapId ?? item.soapId
      if (soapId) {
        const soapLabel = t('Common:VIEW_SOAP')
        menuItems.push({
          key: soapLabel,
          label: soapLabel,
          onClick: (e) => {
            e.stopPropagation()
            persistClickedItem(index)
            handleMenuClose()
            navigate(getSoapUrl(soapId))
          },
        })
      }
    }

    return menuItems
  }

  const menuItems = buildMenuItems()

  return (
    <>
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label={t('Common:ACTIONS')}
        id={buttonId}
        type="button"
        onClick={handleMenuOpen}
      >
        <MoreHoriz />
      </button>
      <Menu
        MenuListProps={
          {
            'aria-labelledby': buttonId,
            'data-click-exclude': 'true',
          } as ExtendedMenuListProps
        }
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        id={menuId}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={(e) => {
          if (
            e &&
            'stopPropagation' in e &&
            typeof e.stopPropagation === 'function'
          ) {
            e.stopPropagation()
          }
          handleMenuClose()
        }}
      >
        {menuItems.map((menuItem) => (
          <MenuItem
            key={menuItem.key}
            role="menuitem"
            onClick={menuItem.onClick}
          >
            <ListItemText>{menuItem.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default RxOrderTableMenu
